import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  SelectionLabel,
  SelectionInput,
  SelectionSelect,
  MultiSelectContainer,
  CheckboxGroup,
  CheckboxItem,
  DateRangeContainer,
  DateInputWrapper,
  UpdateButton,
  QuickDateButtons,
  QuickDateButton,
} from './styles';
import { getQuickDateRange } from './dateRangeUtils';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';

// Dropdown Button Styled Component
const DropdownButton = styled.button`
  padding: 0.35rem 0.6rem;
  font-size: 0.65rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--card-border);
  font-family: 'Poppins', sans-serif;
  background: var(--bg-primary);
  color: var(--text-dark);
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
  font-weight: 600;

  &:hover, &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.1);
  }

  &:active {
    transform: translateY(1px);
  }

  .dropdown-arrow {
    margin-left: 0.35rem;
    transition: transform 0.2s;
    font-size: 0.55rem;
    color: var(--text-gray);
  }

  &.open .dropdown-arrow {
    transform: rotate(180deg);
  }
`;

// Dropdown Card Container
const DropdownCard = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  background: var(--bg-primary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius-md);
  padding: 0.65rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideDown 0.2s ease-out;
  max-height: 80vh;
  overflow-y: auto;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Dropdown Container (relative positioning)
const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

// Save Button (inside filter dropdown cards)
const SaveButton = styled.button`
  padding: 0.3rem 0.5rem;
  border-radius: var(--radius-sm);
  border: none;
  background: var(--accent-orange);
  color: #fff;
  font-family: 'Poppins', sans-serif;
  font-size: var(--button-font-size, 0.6rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  margin-top: 0.5rem;
  width: 100%;
  white-space: nowrap;

  &:hover {
    background: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(255, 150, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Date Range Dropdown Component
interface DateRangeDropdownProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onQuickDateRange: (range: string) => void;
  onUpdate: () => void;
  formatDateForInput?: (date: string) => string;
}

export const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onQuickDateRange,
  onUpdate,
  formatDateForInput = (d) => d,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onStartDateChange(tempStartDate);
    onEndDateChange(tempEndDate);
    onUpdate();
    setIsOpen(false);
  };

  const handleQuickDate = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
  };

  const formatDisplayDate = (date: string): string => {
    if (!date) return 'Not set';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return date;
    }
  };

  const displayText = startDate && endDate 
    ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
    : 'Select Date Range';

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Date Range</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <QuickDateButtons>
            <QuickDateButton onClick={() => handleQuickDate('LAST_7_DAYS')}>
              Last 7 Days
            </QuickDateButton>
            <QuickDateButton onClick={() => handleQuickDate('LAST_30_DAYS')}>
              Last 30 Days
            </QuickDateButton>
            <QuickDateButton onClick={() => handleQuickDate('THIS_MONTH')}>
              This Month
            </QuickDateButton>
            <QuickDateButton onClick={() => handleQuickDate('PREVIOUS_MONTH')}>
              Previous Month
            </QuickDateButton>
            <QuickDateButton onClick={() => handleQuickDate('THIS_SEASON')}>
              This Season
            </QuickDateButton>
          </QuickDateButtons>
          <DateRangeContainer>
            <DateInputWrapper>
              <SelectionLabel>Start Date</SelectionLabel>
              <SelectionInput
                type="date"
                value={formatDateForInput(tempStartDate)}
                onChange={(e) => setTempStartDate(e.target.value)}
                placeholder="DD/MM/YYYY"
              />
            </DateInputWrapper>
            <DateInputWrapper>
              <SelectionLabel>End Date</SelectionLabel>
              <SelectionInput
                type="date"
                value={formatDateForInput(tempEndDate)}
                onChange={(e) => setTempEndDate(e.target.value)}
                placeholder="DD/MM/YYYY"
              />
            </DateInputWrapper>
          </DateRangeContainer>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Athletes Dropdown Component
interface AthletesDropdownProps {
  athletes: Array<{ _id: string; firstName: string; lastName: string; subscription?: { plan?: string } }>;
  selectedAthletes: string[];
  onAthleteToggle: (athleteId: string) => void;
  onSave: (selected: string[]) => void;
  requiredPlan?: 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240';
}

export const AthletesDropdown: React.FC<AthletesDropdownProps> = ({
  athletes,
  selectedAthletes,
  onAthleteToggle,
  onSave,
  requiredPlan = 'hs130',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<string[]>(selectedAthletes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Clean up invalid athlete selections when athletes list changes (e.g., after subscription filtering)
  useEffect(() => {
    const validSelectedAthletes = selectedAthletes.filter(id => 
      athletes.some(athlete => athlete._id === id)
    );
    // Only update if there are invalid selections
    if (validSelectedAthletes.length !== selectedAthletes.length) {
      setTempSelected(validSelectedAthletes);
      onSave(validSelectedAthletes);
    } else {
      setTempSelected(selectedAthletes);
    }
  }, [athletes]); // Only run when athletes list changes, not when selectedAthletes changes
  
  // Update temp selection when selectedAthletes changes from parent
  useEffect(() => {
    setTempSelected(selectedAthletes);
  }, [selectedAthletes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onSave(tempSelected);
    setIsOpen(false);
  };

  const handleToggle = (athleteId: string) => {
    const athlete = athletes.find(a => a._id === athleteId);
    const plan = (athlete as any)?.subscription?.plan || 'free';
    if (!hasPlanOrHigher(plan, requiredPlan)) return;
    setTempSelected(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  // Filter selectedAthletes to only include athletes that are actually in the current athletes list
  // This fixes the bug where old athlete IDs from localStorage might not be in the filtered list
  const validSelectedAthletes = selectedAthletes.filter(id => 
    athletes.some(athlete => athlete._id === id)
  );
  
  const displayText = validSelectedAthletes.length === 0
    ? 'Select Athletes'
    : validSelectedAthletes.length === 1
    ? '1 Athlete Selected'
    : `${validSelectedAthletes.length} Athletes Selected`;

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Select Athletes</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <CheckboxGroup>
            {athletes.map(athlete => {
              const plan = (athlete as any).subscription?.plan || 'free';
              const canSelect = hasPlanOrHigher(plan, requiredPlan);
              return (
                <CheckboxItem key={athlete._id} style={{ opacity: canSelect ? 1 : 0.6 }}>
                  <input
                    type="checkbox"
                    checked={tempSelected.includes(athlete._id)}
                    disabled={!canSelect}
                    onChange={() => handleToggle(athlete._id)}
                  />
                  <span>{athlete.firstName} {athlete.lastName}{!canSelect ? ' (Upgrade plan)' : ''}</span>
                </CheckboxItem>
              );
            })}
          </CheckboxGroup>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Location Dropdown Component
interface LocationDropdownProps {
  locations: string[];
  selectedLocation: string;
  onLocationChange: (location: string) => void;
}

export const LocationDropdown: React.FC<LocationDropdownProps> = ({
  locations,
  selectedLocation,
  onLocationChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempLocation, setTempLocation] = useState(selectedLocation);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempLocation(selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onLocationChange(tempLocation);
    setIsOpen(false);
  };

  const displayText = selectedLocation || 'All Locations';

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Location</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <SelectionSelect
            value={tempLocation}
            onChange={(e) => setTempLocation(e.target.value)}
          >
            <option value="">All Locations</option>
            {locations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </SelectionSelect>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Hill Size Dropdown Component
interface HillSizeDropdownProps {
  hillSizes: string[];
  selectedHillSize: string;
  onHillSizeChange: (hillSize: string) => void;
}

export const HillSizeDropdown: React.FC<HillSizeDropdownProps> = ({
  hillSizes,
  selectedHillSize,
  onHillSizeChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempHillSize, setTempHillSize] = useState(selectedHillSize);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempHillSize(selectedHillSize);
  }, [selectedHillSize]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onHillSizeChange(tempHillSize);
    setIsOpen(false);
  };

  const displayText = selectedHillSize || 'All Hill Sizes';

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Hill Size</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <SelectionSelect
            value={tempHillSize}
            onChange={(e) => setTempHillSize(e.target.value)}
          >
            <option value="">All Hill Sizes</option>
            {hillSizes.map(hillSize => (
              <option key={hillSize} value={hillSize}>{hillSize}</option>
            ))}
          </SelectionSelect>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Session Types Dropdown Component
interface SessionTypesDropdownProps {
  selectedSessionTypes: string[];
  onSessionTypeToggle: (type: string) => void;
  onSave: (types: string[]) => void;
}

export const SessionTypesDropdown: React.FC<SessionTypesDropdownProps> = ({
  selectedSessionTypes,
  onSessionTypeToggle,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<string[]>(selectedSessionTypes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sessionTypeLabels: Record<string, string> = {
    'ski_jumping': 'Training Sessions',
    'competition': 'Competition Sessions',
    'banana_cup': 'Banana Cup Sessions',
  };

  useEffect(() => {
    setTempSelected(selectedSessionTypes);
  }, [selectedSessionTypes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onSave(tempSelected);
    setIsOpen(false);
  };

  const handleToggle = (type: string) => {
    setTempSelected(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const displayText = tempSelected.length === 0
    ? 'Select Session Types'
    : tempSelected.length === 3
    ? 'All Session Types'
    : `${tempSelected.length} Type${tempSelected.length > 1 ? 's' : ''} Selected`;

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Session Types</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <CheckboxGroup>
            {Object.entries(sessionTypeLabels).map(([type, label]) => (
              <CheckboxItem key={type}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(type)}
                  onChange={() => handleToggle(type)}
                />
                <span>{label}</span>
              </CheckboxItem>
            ))}
          </CheckboxGroup>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Equipment Dropdown Component
interface Equipment {
  _id: string;
  name: string;
  athleteId?: string | { _id: string; firstName: string; lastName: string };
}

interface EquipmentDropdownProps {
  equipment: Equipment[];
  selectedEquipment: string[];
  onEquipmentToggle: (equipmentId: string) => void;
  onSave: (selected: string[]) => void;
  athletes: Array<{ _id: string; firstName: string; lastName: string }>;
  label: string;
}

export const EquipmentDropdown: React.FC<EquipmentDropdownProps> = ({
  equipment,
  selectedEquipment,
  onEquipmentToggle,
  onSave,
  athletes,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<string[]>(selectedEquipment);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempSelected(selectedEquipment);
  }, [selectedEquipment]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onSave(tempSelected);
    setIsOpen(false);
  };

  const handleToggle = (equipmentId: string) => {
    setTempSelected(prev =>
      prev.includes(equipmentId)
        ? prev.filter(id => id !== equipmentId)
        : [...prev, equipmentId]
    );
  };

  const displayText = selectedEquipment.length === 0
    ? `Select ${label}`
    : selectedEquipment.length === 1
    ? '1 Selected'
    : `${selectedEquipment.length} Selected`;

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Select {label}</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
        disabled={equipment.length === 0}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          {equipment.length === 0 ? (
            <div style={{ 
              padding: '1rem', 
              textAlign: 'center', 
              color: 'var(--text-gray)', 
              opacity: 0.7,
              fontFamily: 'Poppins',
              fontSize: '0.9rem'
            }}>
              No {label.toLowerCase()} available
            </div>
          ) : (
            <>
              <CheckboxGroup>
                {equipment.map(eq => {
                  const athleteId = typeof eq.athleteId === 'object' ? eq.athleteId._id : eq.athleteId;
                  const athlete = athletes.find(a => a._id === athleteId);
                  const athleteName = athlete ? `${athlete.firstName} ${athlete.lastName}` : 'Unassigned';
                  return (
                    <CheckboxItem key={eq._id}>
                      <input
                        type="checkbox"
                        checked={tempSelected.includes(String(eq._id))}
                        onChange={() => handleToggle(String(eq._id))}
                      />
                      <span>{eq.name} ({athleteName})</span>
                    </CheckboxItem>
                  );
                })}
              </CheckboxGroup>
              <SaveButton onClick={handleSave}>Save</SaveButton>
            </>
          )}
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Gate Dropdown Component
interface GateDropdownProps {
  gates: number[];
  selectedGate: string;
  onGateChange: (gate: string) => void;
}

export const GateDropdown: React.FC<GateDropdownProps> = ({
  gates,
  selectedGate,
  onGateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempGate, setTempGate] = useState(selectedGate);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTempGate(selectedGate);
  }, [selectedGate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onGateChange(tempGate);
    setIsOpen(false);
  };

  const displayText = selectedGate || 'All Gates';

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Gate</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <SelectionSelect
            value={tempGate}
            onChange={(e) => setTempGate(e.target.value)}
          >
            <option value="">All Gates</option>
            {gates.map(gate => (
              <option key={gate} value={String(gate)}>Gate {gate}</option>
            ))}
          </SelectionSelect>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Category Dropdown Component
interface CategoryDropdownProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  selectedCategory,
  onCategoryChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempCategory, setTempCategory] = useState(selectedCategory);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categoryLabels: Record<string, string> = {
    'olympic_games': 'Olympic Games',
    'world_championships': 'World Championships',
    'world_cup': 'World Cup',
    'summer_grand_prix': 'Summer Grand Prix',
    'continental_cup': 'Continental Cup',
    'fis_cup': 'FIS Cup',
    'alpen_cup': 'Alpen Cup',
    'national_championships': 'National Championships',
    'national_cup': 'National Cup',
    'junior_world_championships': 'Junior World Championships',
    'youth_olympic_games': 'Youth Olympic Games',
    'eyof': 'EYOF',
    'opa_games': 'OPA Games',
  };

  useEffect(() => {
    setTempCategory(selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onCategoryChange(tempCategory);
    setIsOpen(false);
  };

  const displayText = selectedCategory ? categoryLabels[selectedCategory] || selectedCategory : 'All Categories';

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Category</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <SelectionSelect
            value={tempCategory}
            onChange={(e) => setTempCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {/* Order: Olympic Games, World Championships, World Cup, Summer Grand Prix, Continental Cup, FIS Cup, Alpen Cup, National Championships, National Cup, Junior World Championships, Youth Olympic Games, EYOF, OPA Games */}
            <option value="olympic_games">Olympic Games</option>
            <option value="world_championships">World Championships</option>
            <option value="world_cup">World Cup</option>
            <option value="summer_grand_prix">Summer Grand Prix</option>
            <option value="continental_cup">Continental Cup</option>
            <option value="fis_cup">FIS Cup</option>
            <option value="alpen_cup">Alpen Cup</option>
            <option value="national_championships">National Championships</option>
            <option value="national_cup">National Cup</option>
            <option value="junior_world_championships">Junior World Championships</option>
            <option value="youth_olympic_games">Youth Olympic Games</option>
            <option value="eyof">EYOF</option>
            <option value="opa_games">OPA Games</option>
          </SelectionSelect>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

// Training Type Dropdown Component (for Ground Training)
interface TrainingTypeDropdownProps {
  selectedTrainingTypes: string[];
  onTrainingTypeToggle: (type: string) => void;
  onSave: (types: string[]) => void;
}

export const TrainingTypeDropdown: React.FC<TrainingTypeDropdownProps> = ({
  selectedTrainingTypes,
  onTrainingTypeToggle,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<string[]>(selectedTrainingTypes);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const trainingTypeLabels: Record<string, string> = {
    'coordination': 'Coordination',
    'power': 'Power',
    'speed': 'Speed',
    'stretching': 'Stretching',
    'specialized_coordination': 'Specialized Coordination',
    'imitation_ski_jump': 'Imitation Ski Jump',
    'run': 'Run',
    'visualisation': 'Visualisation',
  };

  useEffect(() => {
    setTempSelected(selectedTrainingTypes);
  }, [selectedTrainingTypes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSave = () => {
    onSave(tempSelected);
    setIsOpen(false);
  };

  const handleToggle = (type: string) => {
    setTempSelected(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const displayText = tempSelected.length === 0
    ? 'Select Training Types'
    : tempSelected.length === Object.keys(trainingTypeLabels).length
    ? 'All Training Types'
    : `${tempSelected.length} Type${tempSelected.length > 1 ? 's' : ''} Selected`;

  return (
    <DropdownContainer ref={dropdownRef}>
      <SelectionLabel>Training Type</SelectionLabel>
      <DropdownButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={isOpen ? 'open' : ''}
      >
        <span>{displayText}</span>
        <span className="dropdown-arrow">▼</span>
      </DropdownButton>
      {isOpen && (
        <DropdownCard>
          <CheckboxGroup>
            {Object.entries(trainingTypeLabels).map(([type, label]) => (
              <CheckboxItem key={type}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(type)}
                  onChange={() => handleToggle(type)}
                />
                <span>{label}</span>
              </CheckboxItem>
            ))}
          </CheckboxGroup>
          <SaveButton onClick={handleSave}>Save</SaveButton>
        </DropdownCard>
      )}
    </DropdownContainer>
  );
};

